import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * Once the scroll-up sticky header is revealed it stays visible until the user has
 * scrolled down this many times the header's height.
 * @type {number}
 */
const HIDE_DISTANCE_MULTIPLIER = 3;

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Distance scrolled down since the sticky header was last revealed, when sticky behavior is 'scroll-up'
   * @type {number}
   */
  #downScrollDistance = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Suppresses scroll state updates while search modal or account menu is opening/closing
   * @type {boolean}
   */
  #scrollStateSuppressed = false;

  /**
   * Tags the search modal when the header search icon is clicked so CSS can
   * apply the slide-in animation instead of the default fade used by the menu drawer.
   * Registered in capture phase so the class is set before the dialog handler fires.
   * @param {MouseEvent} event
   */
  #onSearchIconClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.search-action button')) return;
    document.getElementById('search-modal')?.classList.add('search-modal--from-header');
  };

  #disableScrollStateUpdate = () => {
    this.#scrollStateSuppressed = true;
  };

  #onSearchModalClose = () => {
    document.getElementById('search-modal')?.classList.remove('search-modal--from-header');
    this.#enableScrollStateUpdate();
  };

  #enableScrollStateUpdate = () => {
    requestAnimationFrame(() => {
      this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
      this.#downScrollDistance = 0;
      if (this.#scrollRafId !== null) {
        cancelAnimationFrame(this.#scrollRafId);
        this.#scrollRafId = null;
      }
      this.#scrollStateSuppressed = false;
    });
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  /** @param {Event} event */
  #onDrawerToggle = (event) => {
    const drawer = /** @type {HTMLDetailsElement} */ (event.currentTarget);
    document.documentElement.classList.toggle('menu-drawer-open', drawer.open);
  };

  /**
   * Mirrors the sticky state onto <html> for rules outside the header.
   * @param {string} state
   */
  #setStickyState(state) {
    this.dataset.stickyState = state;
    document.documentElement.classList.toggle('header-sticky-active', state === 'active');
  }

  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.#setStickyState(isIntersecting ? 'inactive' : 'active');
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
      this.#updateSearchOffset();
    });
  };

  #updateScrollState = () => {
    if (this.#scrollStateSuppressed) return;

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.#downScrollDistance = 0;

      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.#setStickyState('inactive');
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up — only the nav bar is visible, not the announcement bar
        this.#setStickyState('active');
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      // keep the revealed header visible until the user has clearly scrolled down
      this.#downScrollDistance += scrollTop - this.#lastScrollTop;

      if (this.#downScrollDistance >= this.offsetHeight * HIDE_DISTANCE_MULTIPLIER) {
        this.#downScrollDistance = 0;
        this.dataset.scrollDirection = 'none';
        this.#setStickyState('idle');
      }
    } else {
      this.dataset.scrollDirection = 'none';
      this.#setStickyState('idle');
    }

    this.#lastScrollTop = scrollTop;
  };

  #updateSearchOffset() {
    const height = Math.max(0, Math.round(this.getBoundingClientRect().bottom));
    document.body.style.setProperty('--header-search-top-offset', `${height}px`);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    document.addEventListener('click', this.#onSearchIconClick, true);

    const searchModal = document.getElementById('search-modal');
    searchModal?.addEventListener('dialog:open', this.#disableScrollStateUpdate);
    searchModal?.addEventListener('dialog:close', this.#onSearchModalClose);

    const accountMenu = document.querySelector('account-menu');
    accountMenu?.addEventListener('dialog:open', this.#disableScrollStateUpdate);
    accountMenu?.addEventListener('dialog:close', this.#enableScrollStateUpdate);

    // Derived from the attribute, which article-quick-search.js strips at runtime.
    document.documentElement.classList.toggle('header-sticky', this.hasAttribute('sticky'));

    const drawer = document.getElementById('Details-menu-drawer-container');
    drawer?.addEventListener('toggle', this.#onDrawerToggle);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    document.getElementById('Details-menu-drawer-container')?.removeEventListener('toggle', this.#onDrawerToggle);
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('click', this.#onSearchIconClick, true);

    const searchModal = document.getElementById('search-modal');
    searchModal?.removeEventListener('dialog:open', this.#disableScrollStateUpdate);
    searchModal?.removeEventListener('dialog:close', this.#onSearchModalClose);

    const accountMenu = document.querySelector('account-menu');
    accountMenu?.removeEventListener('dialog:open', this.#disableScrollStateUpdate);
    accountMenu?.removeEventListener('dialog:close', this.#enableScrollStateUpdate);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
      document.body.style.setProperty(
        '--header-search-top-offset',
        `${roundedHeaderGroupHeight}px`
      );
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
